import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { membershipAPI, type MembershipItemDto } from '@matix/api-client';
import { useUI } from '../../store/ui';
import { useCart } from '../../store/cart';
import type { MockProduct } from '../../lib/mock-data';
import { fmt } from '../../lib/mock-data';
import { toast } from '../../store/toast';
import { XIcon, PackageIcon } from '../../components/icons';

/**
 * View Membership — the redeem side of the membership feature (mirrors the
 * legacy "card membership" link). For the cart's selected cash customer it
 * lists their live prepaid packages; picking one shows its remaining items,
 * and tapping an item adds it to the cart for free (a membership-redemption
 * line that draws down that prepaid Inv_MembershipDetail on checkout).
 *
 * Self-gates on the 'membership' modal id and renders nothing when closed.
 */
export function MembershipModal(): JSX.Element | null {
  const isOpen = useUI((s) => s.openModal === 'membership');
  const closeModal = useUI((s) => s.closeModal);
  const customer = useCart((s) => s.customer);
  const addMembershipLine = useCart((s) => s.addMembershipLine);

  const customerId = customer?.type === 'cash' ? customer.id : null;

  const query = useQuery({
    queryKey: ['memberships', customerId],
    queryFn: () => membershipAPI.forCustomer(customerId as number),
    enabled: isOpen && customerId != null,
    staleTime: 0,
  });

  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Local draw-down so the remaining count drops as the cashier taps items,
  // before the order is charged. Keyed by membership detail id.
  const [used, setUsed] = useState<Record<number, number>>({});

  // Reset transient state each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setSelectedId(null);
      setUsed({});
    }
  }, [isOpen]);

  const memberships = query.data ?? [];
  const selected = useMemo(
    () => memberships.find((m) => m.membership_id === selectedId) ?? null,
    [memberships, selectedId],
  );

  if (!isOpen) return null;

  function remainingOf(item: MembershipItemDto): number {
    return Math.max(0, item.remaining_qty - (used[item.detail_id] ?? 0));
  }

  function addItem(item: MembershipItemDto): void {
    if (remainingOf(item) <= 0) return;
    const product: MockProduct = {
      id: item.item_id,
      name: item.item_name ?? `#${item.item_id}`,
      nameAr: item.item_name_ar ?? '',
      price: 0,
      cat: '',
      emoji: '🎟️',
      bg: 'var(--surface-3, #f1f5f9)',
      station: '',
    };
    addMembershipLine(product, item.detail_id);
    setUsed((u) => ({ ...u, [item.detail_id]: (u[item.detail_id] ?? 0) + 1 }));
    toast(`${product.name} added from membership`, 'success');
  }

  return (
    <div style={overlay} onClick={closeModal}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            {selected
              ? `${selected.customer_name ?? 'Customer'} — package items`
              : 'Membership'}
          </div>
          <button type="button" style={iconBtn} onClick={closeModal} aria-label="Close">
            <XIcon size={18} />
          </button>
        </div>

        <div style={body}>
          {customerId == null ? (
            <Empty text="Select a cash customer first to view their memberships." />
          ) : query.isLoading ? (
            <Empty text="Loading…" />
          ) : query.isError ? (
            <Empty text="Couldn't load memberships. Check your connection." />
          ) : memberships.length === 0 ? (
            <Empty text="This customer has no active memberships." />
          ) : !selected ? (
            // ── Package list ────────────────────────────────────────────
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {memberships.map((m) => (
                <button
                  key={m.membership_id}
                  type="button"
                  style={packageRow}
                  onClick={() => setSelectedId(m.membership_id)}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      Package #{m.membership_id}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted, #71717a)' }}>
                      {m.items.length} item{m.items.length === 1 ? '' : 's'} · expires{' '}
                      {formatDate(m.expiration_date)}
                    </div>
                  </div>
                  <span style={{ color: 'var(--accent, #2563eb)', fontSize: 13 }}>Open →</span>
                </button>
              ))}
            </div>
          ) : (
            // ── Item grid for the selected package ──────────────────────
            <>
              <button type="button" style={backLink} onClick={() => setSelectedId(null)}>
                ← All packages
              </button>
              <div style={grid}>
                {selected.items.map((item) => {
                  const remaining = remainingOf(item);
                  return (
                    <button
                      key={item.detail_id}
                      type="button"
                      disabled={remaining <= 0}
                      style={{ ...itemCard, opacity: remaining <= 0 ? 0.45 : 1 }}
                      onClick={() => addItem(item)}
                    >
                      <PackageIcon size={22} />
                      <div style={{ fontWeight: 600, fontSize: 13, textAlign: 'center' }}>
                        {item.item_name ?? `#${item.item_id}`}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted, #71717a)' }}>
                        {remaining} left{item.unit_name ? ` · ${item.unit_name}` : ''}
                      </div>
                      <div style={freeTag}>{fmt(0)}</div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Empty(props: { text: string }): JSX.Element {
  return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted, #71717a)' }}>
      {props.text}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Inline styles (self-contained; no CSS dependency) ───────────────────────
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
};
const panel: React.CSSProperties = {
  width: 'min(640px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
  background: 'var(--surface-1, #fff)', color: 'var(--text, #18181b)',
  borderRadius: 12, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
};
const header: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 18px', borderBottom: '1px solid var(--border, #e4e4e7)',
};
const body: React.CSSProperties = { padding: 16, overflowY: 'auto' };
const iconBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: 4,
};
const packageRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border, #e4e4e7)',
  background: 'transparent', cursor: 'pointer', textAlign: 'left', color: 'inherit',
};
const backLink: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--accent, #2563eb)',
  fontSize: 13, padding: '0 0 12px', fontWeight: 600,
};
const grid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10,
};
const itemCard: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
  padding: '14px 10px', borderRadius: 10, border: '1px solid var(--border, #e4e4e7)',
  background: 'var(--surface-2, #fafafa)', cursor: 'pointer', color: 'inherit',
};
const freeTag: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: 'var(--success, #16a34a)',
};
